
import React from 'react';
import * as XLSX from 'xlsx';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { fetchAllPages, TableName } from './exportUtils';

interface TableExportProps {
  tableName: TableName;
  prettyName: string;
  isExporting: boolean;
  setIsExporting: (value: boolean) => void;
}

export const TableExport = ({ tableName, prettyName, isExporting, setIsExporting }: TableExportProps) => {
  const { toast } = useToast();

  const downloadTableAsCSV = async () => {
    try {
      setIsExporting(true);
      
      const data = await fetchAllPages(tableName);
      
      if (!data) throw new Error('No data received');

      const ws = XLSX.utils.json_to_sheet(data);
      const csv = XLSX.utils.sheet_to_csv(ws);
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      link.href = URL.createObjectURL(blob);
      link.download = `${prettyName.toLowerCase()}_export_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast({
        title: "Export Complete",
        description: `${prettyName} exported successfully to CSV`,
      });
    } catch (error) {
      console.error('CSV export error:', error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: `Failed to export ${prettyName}. Please try again.`,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      onClick={downloadTableAsCSV}
      disabled={isExporting}
      variant="outline"
      className="w-full"
    >
      {isExporting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Exporting...
        </>
      ) : (
        `Export ${prettyName} (CSV)`
      )}
    </Button>
  );
};
